// src/pages/Multiplayer.tsx
// Online Players panel + 3 game panels with modal and hosting state

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.tsx";
import { PageHeader } from "../components/PageHeader.tsx";
import { OnlineUsersPanel } from "../components/multiplayer/OnlineUsersPanel.tsx";
import { CreateGamePanel } from "../components/multiplayer/CreateGamePanel.tsx";
import { HostLobbyPanel } from "../components/multiplayer/HostLobbyPanel.tsx";
import { JoinGamePanel } from "../components/multiplayer/JoinGamePanel.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
import { useCustomVariants } from "../hooks/useCustomVariants.ts";
import io, { Socket } from "socket.io-client";

interface GameConfig {
  gameName: string;
  presetId: string;
  variantId?: string;
}

// Hosted game info from API
interface HostedGame {
  roomId: string;
  presetId?: string;
  gameName?: string;
  boardSize: number;
  opponentJoined: boolean;
  opponentUsername?: string;
  opponentStats?: {
    rank: number;
    rating: number;
    gamesPlayed: number;
    winsPercentage: number;
  };
}

// Game preset mapping for display
const presetNames: Record<string, string> = {
  "0808-001": "Traditional FIDE Chess (8×8)",
  "0909-001": "Perfect Chess (9×9)",
  "1010-001": "Breakout Chess (10×10)",
  "1111-001": "Next Level Chess (11×11)",
  "1212-001": "Arrival Chess (12×12)",
};

export const Multiplayer: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.userId || "";
  const currentUsername = user?.username || "Guest";

  // Fetch user's saved custom variants for Create Game panel
  const { customVariants } = useCustomVariants();

  // Socket connection
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [createdGame, setCreatedGame] = useState<GameConfig | null>(null);

  // Challenge modal state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeTarget, setChallengeTarget] = useState<{ userId: string; username: string } | null>(null);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 5000);
  };

  // Hosting state - track multiple games
  const [hostedGames, setHostedGames] = useState<Map<string, HostedGame>>(new Map());
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

  // Derived state from selected game
  const selectedGame = selectedRoomId ? hostedGames.get(selectedRoomId) : null;
  const isHosting = selectedGame !== null && selectedGame !== undefined;
  const roomId = selectedRoomId;
  const opponentJoined = selectedGame?.opponentJoined || false;
  const opponentUsername = selectedGame?.opponentUsername || null;
  const opponentStats = selectedGame?.opponentStats || null;

  // Initialize socket connection
  useEffect(() => {
    if (!user?.userId || !user?.username) return;

    const SERVER_URL = process.env.REACT_APP_API_URL || "";

    const socket = io(SERVER_URL, {
      auth: {
        userId: user.userId,
        username: user.username,
      },
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Socket connected for multiplayer");
      setIsConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("❌ Socket disconnected");
      setIsConnected(false);
    });

    // Listen for room creation response
    socket.on("room_created", (data: { roomId: string; color: string; presetId?: string; boardSize?: number; gameName?: string }) => {
      console.log(`🎮 Room created: ${data.roomId}`);

      // Add the new game to hosted games
      setHostedGames(prev => {
        const newMap = new Map(prev);
        newMap.set(data.roomId, {
          roomId: data.roomId,
          presetId: data.presetId,
          gameName: data.gameName,
          boardSize: data.boardSize || 8,
          opponentJoined: false,
        });
        return newMap;
      });

      // Select the newly created game
      setSelectedRoomId(data.roomId);
      setShowModal(true);
    });

    // Listen for opponent joining
    socket.on("opponent_joined", (data: {
      roomId?: string;
      odisplayName?: string;
      username?: string;
      odisplayRank?: number;
      odisplayRating?: number;
      odisplayGamesPlayed?: number;
      odisplayWinsPercentage?: number;
    }) => {
      const oppName = data.odisplayName || data.username || "Opponent";
      console.log(`👥 Opponent joined: ${oppName} in room: ${data.roomId}`);

      // Update the specific game in hosted games
      setHostedGames(prev => {
        const newMap = new Map(prev);
        // If roomId is provided, update that room; otherwise update the selected room
        const targetRoomId = data.roomId || selectedRoomId;
        if (targetRoomId) {
          const game = newMap.get(targetRoomId);
          if (game) {
            newMap.set(targetRoomId, {
              ...game,
              opponentJoined: true,
              opponentUsername: oppName,
              opponentStats: {
                rank: data.odisplayRank || 0,
                rating: data.odisplayRating || 1200,
                gamesPlayed: data.odisplayGamesPlayed || 0,
                winsPercentage: data.odisplayWinsPercentage || 0,
              },
            });
          }
        }
        return newMap;
      });

      showToast(`${oppName} has joined your game!`);
    });

    // Listen for challenge received
    socket.on("challenge_received", (data: { from: { userId: string; username: string } }) => {
      showToast(`⚔️ ${data.from.username} has challenged you to a game!`);
    });

    // Listen for successful room join
    socket.on("room_joined", (data: { roomId: string; color: string; gameState?: any }) => {
      console.log(`✅ Joined room: ${data.roomId} as ${data.color}`);
      showToast(`Joined game as ${data.color}!`);
    });

    // Listen for game started - navigate to play page
    socket.on("game_started", (data: {
      roomId: string;
      gameState?: any;
      boardSize?: number;
      variantId?: string;
      presetId?: string;
      whitePlayer?: string;
      blackPlayer?: string;
    }) => {
      console.log(`🎮 Game started! Room: ${data.roomId}`);
      console.log(`   Preset ID: ${data.presetId}`);
      console.log(`   Board Size: ${data.boardSize}`);
      showToast("Game started! Loading game...");

      // Determine player color - host is white, joiner is black
      const myColor = data.whitePlayer === currentUsername ? "white" : "black";
      console.log(`   My color: ${myColor} (currentUsername: ${currentUsername})`);

      // Navigate to play page with room data
      navigate("/play-online", {
        state: {
          roomId: data.roomId,
          gameState: data.gameState,
          boardSize: data.boardSize,
          variantId: data.variantId,
          presetId: data.presetId,
          whitePlayer: data.whitePlayer,
          blackPlayer: data.blackPlayer,
          isMultiplayer: true,
          playerColor: myColor,
        }
      });
    });

    // Listen for errors
    socket.on("error", (data: { message: string }) => {
      showToast(`Error: ${data.message}`);
    });

    // Listen for room deleted confirmation
    socket.on("room_deleted", (data: { roomId: string }) => {
      console.log(`🗑️ Room deleted: ${data.roomId}`);
      // Remove from hosted games
      setHostedGames(prev => {
        const newMap = new Map(prev);
        newMap.delete(data.roomId);
        return newMap;
      });
      // If the deleted room was selected, clear selection
      setSelectedRoomId(prev => prev === data.roomId ? null : prev);
      showToast(`Game ${data.roomId} has been cancelled`);
    });

    return () => {
      socket.close();
    };
  }, [user?.userId, user?.username, navigate]);

  const handleChallengeUser = (userId: string, username: string) => {
    setChallengeTarget({ userId, username });
    setShowChallengeModal(true);
  };

  const confirmChallenge = () => {
    if (challengeTarget && socketRef.current) {
      socketRef.current.emit("challenge_user", {
        targetUserId: challengeTarget.userId
      });
      showToast(`Challenge sent to ${challengeTarget.username}! Waiting for response...`);
      console.log(`⚔️ Challenge sent to ${challengeTarget.username} (${challengeTarget.userId})`);
    } else if (!socketRef.current) {
      showToast("Not connected to server. Please refresh the page.");
    }
    setShowChallengeModal(false);
    setChallengeTarget(null);
  };

  const handleJoinGame = (userId: string, username: string, gameVariant?: string) => {
    showToast(`Joining ${username}'s game: ${gameVariant || 'Unknown variant'}`);
    // TODO: Implement join via socket - need room ID
    console.log(`🎮 Joining ${username}'s game (variant: ${gameVariant})`);
  };

  const handleCreateGame = (config: GameConfig) => {
    console.log("Creating game with config:", config);

    if (!socketRef.current || !isConnected) {
      showToast("Not connected to server. Please refresh the page.");
      return;
    }

    // Extract board size from preset ID (e.g., "0808-001" -> 8)
    const boardSize = parseInt(config.presetId.substring(0, 2), 10) || 8;

    // Set local state first
    setCreatedGame(config);

    // Send create room request to server
    socketRef.current.emit("create_room", {
      userId: currentUserId,
      username: currentUsername,
      variantId: config.variantId,
      presetId: config.presetId, // Pass preset ID for board setup
      boardSize: boardSize,
      gameName: config.gameName,
      initialGameState: {}, // Empty initial state - server can generate
    });

    showToast("Creating game room...");
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleStartGame = () => {
    console.log("🎮 handleStartGame called");
    console.log("   opponentJoined:", opponentJoined);
    console.log("   isConnected:", isConnected);
    console.log("   roomId:", roomId);

    if (!opponentJoined) {
      showToast("Waiting for opponent to join...");
      return;
    }

    if (!socketRef.current || !isConnected) {
      showToast("Not connected to server. Please refresh the page.");
      return;
    }

    if (!roomId) {
      showToast("No active game room.");
      return;
    }

    console.log("🎮 Emitting start_game event for room:", roomId);
    showToast("Starting game...");
    socketRef.current.emit("start_game", { roomId });
  };

  const handleCancelHosting = () => {
    if (!selectedRoomId) {
      showToast("No game selected to cancel.");
      return;
    }

    if (!socketRef.current || !isConnected) {
      showToast("Not connected to server. Please refresh the page.");
      return;
    }

    // Emit delete_room to server - server will respond with room_deleted
    console.log(`🗑️ Requesting to delete room: ${selectedRoomId}`);
    socketRef.current.emit("delete_room", {
      roomId: selectedRoomId,
      userId: currentUserId,
    });

    // Clear selection immediately for better UX
    setSelectedRoomId(null);
    setCreatedGame(null);
  };

  // Handler for selecting own game from Join Game panel
  const handleSelectOwnGame = (game: { roomId: string; presetId?: string; gameName?: string; boardSize: number }) => {
    // Check if we already have this game tracked
    if (!hostedGames.has(game.roomId)) {
      // Add it to hosted games
      setHostedGames(prev => {
        const newMap = new Map(prev);
        newMap.set(game.roomId, {
          roomId: game.roomId,
          presetId: game.presetId,
          gameName: game.gameName,
          boardSize: game.boardSize,
          opponentJoined: false,
        });
        return newMap;
      });
    }
    // Select this game to show in Host Lobby
    setSelectedRoomId(game.roomId);
    // Update createdGame for modal/display purposes
    if (game.presetId) {
      setCreatedGame({
        gameName: game.gameName || "",
        presetId: game.presetId,
      });
    }
    showToast(`Selected game ${game.roomId} to manage`);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", paddingTop: "10px" }}>
      <PageHeader
        icon={TAB_DESCRIPTIONS.multiplayer.icon}
        title={TAB_DESCRIPTIONS.multiplayer.label}
        description={TAB_DESCRIPTIONS.multiplayer.description}
      />

      <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "20px" }}>

        {/* Online Players Panel - Green */}
        <div style={{
          background: "white",
          borderRadius: "12px",
          border: "3px solid #4CAF50",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          overflow: "hidden",
          marginTop: "20px"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
            padding: "15px 20px",
            borderBottom: "2px solid #2E7D32"
          }}>
            <h3 style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: 700,
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <span style={{ fontSize: "24px" }}>👥</span>
              Online Players
            </h3>
          </div>
          <div style={{ padding: "15px 20px", minHeight: "150px" }}>
            <OnlineUsersPanel
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              serverUrl={process.env.REACT_APP_API_URL || ""}
              onChallengeUser={handleChallengeUser}
              onJoinGame={handleJoinGame}
              isCurrentUserHosting={isHosting}
              currentUserGameVariant={createdGame ? (presetNames[createdGame.presetId] || createdGame.presetId) : undefined}
              onShowToast={showToast}
            />
          </div>
        </div>

        {/* Three panels row: Create Game, Host Lobby, Join Game */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "20px",
          marginTop: "20px"
        }}>

          {/* Create Game Panel - Orange */}
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "3px solid #FF9800",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            <div style={{
              background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
              padding: "15px 20px",
              borderBottom: "2px solid #E65100"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <span style={{ fontSize: "24px" }}>➕</span>
                Create Game
              </h3>
            </div>
            <div style={{ padding: "20px" }}>
              <CreateGamePanel
                onCreateGame={handleCreateGame}
                userVariants={customVariants.map(v => ({
                  id: v.id,
                  name: `${v.name} (${v.boardSize}×${v.boardSize})`
                }))}
              />
            </div>
          </div>

          {/* Host Lobby Panel - Blue */}
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "3px solid #2196F3",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            <div style={{
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              padding: "15px 20px",
              borderBottom: "2px solid #0D47A1"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <span style={{ fontSize: "24px" }}>🏠</span>
                Host Lobby
              </h3>
            </div>
            <div style={{ padding: "20px" }}>
              <HostLobbyPanel
                isHosting={isHosting}
                roomId={roomId}
                gameVariant={selectedGame?.presetId ? (presetNames[selectedGame.presetId] || selectedGame.presetId) : (createdGame ? (presetNames[createdGame.presetId] || createdGame.presetId) : undefined)}
                opponentJoined={opponentJoined}
                opponentUsername={opponentUsername}
                opponentStats={opponentStats}
                onStartGame={handleStartGame}
                onCancelHosting={handleCancelHosting}
              />
            </div>
          </div>

          {/* Join Game Panel - Blue */}
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "3px solid #2196F3",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            <div style={{
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              padding: "15px 20px",
              borderBottom: "2px solid #0D47A1"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <span style={{ fontSize: "24px" }}>🎮</span>
                Join Game
              </h3>
            </div>
            <div style={{ padding: "20px" }}>
              <JoinGamePanel
                onJoinGame={(joinRoomId) => {
                  if (!socketRef.current || !isConnected) {
                    showToast("Not connected to server. Please refresh the page.");
                    return;
                  }
                  showToast(`Joining game room: ${joinRoomId}...`);
                  socketRef.current.emit("join_room", {
                    roomId: joinRoomId,
                    userId: currentUserId,
                    username: currentUsername,
                  });
                }}
                onSelectOwnGame={handleSelectOwnGame}
                currentUserId={currentUserId}
                serverUrl={process.env.REACT_APP_API_URL || ""}
              />
            </div>
          </div>

        </div>

      </div>

      {/* Game Created Modal - Centered */}
      {showModal && createdGame && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "3px solid #4CAF50",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            maxWidth: "500px",
            width: "90%",
            overflow: "hidden"
          }}>
            {/* Modal Header - Green */}
            <div style={{
              background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
              padding: "20px 24px",
              borderBottom: "2px solid #2E7D32"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}>
                <span style={{ fontSize: "28px" }}>✓</span>
                Game Created Successfully!
              </h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px" }}>
              {/* Game Details */}
              <div style={{
                background: "#f5f5f5",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px"
              }}>
                <div style={{ marginBottom: "12px" }}>
                  <span style={{ fontSize: "14px", color: "#666", fontWeight: 600 }}>Game Mode:</span>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "#333", marginTop: "4px" }}>
                    {presetNames[createdGame.presetId] || createdGame.presetId}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: "14px", color: "#666", fontWeight: 600 }}>Room Code:</span>
                  <div style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#4CAF50",
                    marginTop: "4px",
                    fontFamily: "monospace",
                    letterSpacing: "2px"
                  }}>
                    {roomId}
                  </div>
                </div>
              </div>

              {/* Info Text */}
              <p style={{
                fontSize: "15px",
                color: "#666",
                margin: "0 0 20px 0",
                lineHeight: 1.5
              }}>
                Your game lobby is now active! Share the room code with your opponent,
                or wait for someone to join. You can see your lobby in the <strong>Host Lobby</strong> panel.
              </p>

              {/* Close Button */}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "14px 40px",
                    fontSize: "16px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                    transition: "all 0.2s"
                  }}
                >
                  Got It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Challenge Confirmation Modal */}
      {showChallengeModal && challengeTarget && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            border: "3px solid #4CAF50",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            maxWidth: "450px",
            width: "90%",
            overflow: "hidden"
          }}>
            {/* Modal Header */}
            <div style={{
              background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
              padding: "20px 24px",
              borderBottom: "2px solid #2E7D32"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}>
                <span style={{ fontSize: "26px" }}>⚔️</span>
                Challenge Player
              </h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px" }}>
              <p style={{
                fontSize: "16px",
                color: "#333",
                margin: "0 0 20px 0",
                textAlign: "center",
                lineHeight: 1.5
              }}>
                Challenge <strong style={{ color: "#4CAF50" }}>{challengeTarget.username}</strong> to a game?
              </p>
              <p style={{
                fontSize: "14px",
                color: "#666",
                margin: "0 0 24px 0",
                textAlign: "center"
              }}>
                They will receive an invitation to play against you.
              </p>

              {/* Buttons */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <button
                  onClick={() => {
                    setShowChallengeModal(false);
                    setChallengeTarget(null);
                  }}
                  style={{
                    background: "#e0e0e0",
                    color: "#333",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 32px",
                    fontSize: "15px",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmChallenge}
                  style={{
                    background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 32px",
                    fontSize: "15px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)"
                  }}
                >
                  Send Challenge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            backgroundColor: "#333",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            zIndex: 10001,
            fontSize: "14px",
            fontWeight: "bold",
            animation: "fadeIn 0.3s",
          }}
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default Multiplayer;
